import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, tap, take } from 'rxjs';
import { Navigation } from 'app/core/navigation/navigation.types';
import { defaultNavigation } from 'app/mock-api/common/navigation/data'; // Import de la constante du fichier data.ts

import { FuseNavigationItem } from '@fuse/components/navigation';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

  constructor(private _httpClient: HttpClient) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation
   */
  get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Observable<Navigation> {
    return this._httpClient.get<Navigation>('api/common/navigation').pipe(
      tap(navigation => {
        this._navigation.next(navigation);
      })
    );
  }

  // Fonction générique pour rechercher un élément dans defaultNavigation par ID
  findItemById(id: string, items: any[]): any | null {
    for (let item of items) {
      if (item.id === id) {
        return item;
      }
      if (item.children) {
        const foundChild = this.findItemById(id, item.children); // Recherche récursive dans les enfants
        if (foundChild) {
          return foundChild;
        }
      }
    }
    return null; // Si l'élément n'est pas trouvé
  }

  // Nouvelle fonction pour récupérer les enfants d'un élément par ID
  getChildrenById(id: string): any[] {
    if (id === 'home') {
      // Si l'ID est 'home', retourner tous les éléments situés à la racine sauf 'home'
      let children: any[] = [];
      this.navigation$.subscribe(data => {
        const navigation = data.default;
        children = navigation.filter(
          navItem => navItem.id !== 'home' && (!navItem.hidden || !navItem.hidden(navItem)) // Vérifie si l'élément est visible
        );
      });
      return children;
    }

    // Sinon, rechercher l'élément et retourner ses enfants
    const item = this.findItemById(id, defaultNavigation);
    return item && item.children ? item.children : []; // Retourne les enfants ou un tableau vide
  }

  // Nouvelle fonction pour récupérer tous les parents d'un élément par ID
  getParentsById(id: string): any[] {
    const parents = [];
    let currentItem = this.findItemById(id, defaultNavigation);

    while (currentItem) {
      const parent = this.findParentByChildId(currentItem.id, defaultNavigation);
      if (parent) {
        parents.unshift(parent); // Ajouter le parent en début de tableau
        currentItem = parent; // Remonter au parent
      } else {
        currentItem = null; // Fin de la remontée
      }
    }

    return parents;
  }

  // Fonction pour trouver le parent direct d'un élément donné
  private findParentByChildId(childId: string, items: any[]): any | null {
    for (let item of items) {
      if (item.children && item.children.some(child => child.id === childId)) {
        return item; // Retourne l'élément parent si l'enfant est trouvé
      }
      if (item.children) {
        const parent = this.findParentByChildId(childId, item.children); // Recherche récursive
        if (parent) {
          return parent;
        }
      }
    }
    return null;
  }

  getItemById(id: string): any {
    const item = this.findItemById(id, defaultNavigation); // Appel de la fonction de recherche
    return item;
  }

  isExternalLink(id: string): boolean {
    //const item = defaultNavigation.find(navItem => navItem.id === id);
    const item = this.findItemById(id, defaultNavigation); // Appel de la fonction de recherche
    return item ? (item.externalLink ? item.externalLink : false) : false; // Retourner false par défaut si l'ID n'est pas trouvé
  }

  getNavigationLinkById(id: string): string {
    //const item = defaultNavigation.find(navItem => navItem.id === id);
    const item = this.findItemById(id, defaultNavigation); // Appel de la fonction de recherche
    const link = item ? item.link : '/';
    // Toujours retourner un chemin absolu pour éviter les concaténations relatives du router
    if (!link) {
      return '/';
    }
    return link.startsWith('/') ? link : `/${link}`;
  }

  getIconById(id: string): string {
    const item = this.findItemById(id, defaultNavigation); // Appel de la fonction de recherche
    return item
      ? item.icon
        ? item.icon
        : 'heroicons_outline:chevron-right'
      : 'heroicons_outline:chevron-right'; // Retourner 'heroicons_outline:chevron-right' par défaut si l'ID n'est pas trouvé ou si l'icone n'est pas renseigné
  }

  getTitleById(id: string): string {
    const item = this.findItemById(id, defaultNavigation); // Appel de la fonction de recherche
    return item ? item.title : ''; // Retourner 'Titre par défaut' par défaut si l'ID n'est pas trouvé
  }

  getDescriptionById(id: string): string {
    const item = this.findItemById(id, defaultNavigation); // Appel de la fonction de recherche
    return item ? (item.description ? item.description : item.title) : ''; // Retourner le subtitle sinon le title, vide si l'ID n'est pas trouvé
  }

  // Méthode pour construire les breadcrumbs en remontant la hiérarchie à partir d'un Id, Ajoût de 'home' et de l'id courant
  buildBreadcrumbs(id: string): any[] {
    const breadcrumbs = [];
    //Ajout de l'élément 'home'
    if (id != 'home') {
      breadcrumbs.push({
        dataNavigationId: 'home'
      });
    }
    // Ajouter les parents en utilisant getParentsById
    const parents = this.getParentsById(id);
    parents.forEach(parent => {
      breadcrumbs.push({
        dataNavigationId: parent.id
      });
    });

    // Ajouter l'élément actuel à la fin des breadcrumbs
    const currentItem = this.findItemById(id, defaultNavigation);
    if (currentItem) {
      breadcrumbs.push({
        dataNavigationId: currentItem.id
      });
    }

    return breadcrumbs;
  }

  //Méthode pour construire un tableau de cards à partir d'un Id en parcourant les enfants
  buildCards(id: string): any[] {
    let cards: any[] = [];
    // Appeler le service pour récupérer les enfants de 'configuration'
    const childrens = this.getChildrenById(id);
    // filter si l'élément est caché
    const visibleChildrens = childrens.filter(child => !child.hidden || !child.hidden(child));
    // Parcourir les enfants pour alimenter le tableau 'cards'
    cards = visibleChildrens.map(child => ({
      dataNavigationId: child.id // Utiliser l'ID comme dataNavigationId
    }));
    return cards;
  }

  updateNavigation(): void {
    this.get().subscribe(data => {
      // Mise à jour du BehaviorSubject
      this._navigation.next(data);
    });
  }

  updateNavigationItems(newNavigation: FuseNavigationItem[]): void {
    this.navigation$.pipe(take(1)).subscribe(data => {
      data.default = newNavigation;
      this._navigation.next(data);
    });
  }
}
